import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import MyThingsNav from "./my-things-nav";
import { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Service from "../webservice/http";
import { storage } from "../utils/storage";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import "react-notifications/lib/notifications.css";
import "../../src/task.css";
import ClipLoader from "react-spinners/ClipLoader";

const INITIAL_CATEGORIES = [
  {
    id: 1,
    title: "Call Opening",
    expanded: true,
    items: [
      { id: 101, label: "Call Open Timely Manner" },
      { id: 102, label: "Standard Opening Spiel" },
      { id: 103, label: "Verification Security" },
    ],
  },
  {
    id: 2,
    title: "Customer Experience",
    expanded: true,
    items: [
      { id: 201, label: "Friendly Confident Tone" },
      { id: 202, label: "Attentive Listening" },
      { id: 203, label: "Call Control Efficiency" },
    ],
  },
  {
    id: 3,
    title: "Process Compliance",
    expanded: true,
    items: [
      { id: 301, label: "Follow Policies Procedure" },
      { id: 302, label: "Service Reminder" },
      { id: 303, label: "Customer Alternative Number" },
    ],
  },
  {
    id: 4,
    title: "Negotiation",
    expanded: true,
    items: [
      { id: 401, label: "Probing Questions Effectiveness" },
      { id: 402, label: "Payment Resolution Actions" },
      { id: 403, label: "Payment Delay Consequences" },
    ],
  },
  {
    id: 5,
    title: "Call Closing",
    expanded: true,
    items: [
      { id: 501, label: "Call Recap" },
      { id: 502, label: "Additional Queries" },
      { id: 503, label: "Call Closed Properly" },
    ],
  },
  {
    id: 6,
    title: "Documentation",
    expanded: true,
    items: [
      { id: 601, label: "Call Notes Documented Accurately" },
      { id: 602, label: "Correct Disposition Code Applied" },
    ],
  },
  {
    id: 7,
    title: "Service Compliance",
    expanded: true,
    items: [
      { id: 701, label: "Customer Treated with Respect" },
      { id: 702, label: "Regulatory Disclosures Made" },
    ],
  },
];

let nextId = 2000;

const MyThing = () => {
  const services = useMemo(() => new Service(), []);
  const history = useHistory();
  const role = storage.getItem("role");

  const [showCallList, setShowCallList] = useState(true);

  const [calls, setCalls] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsImporting, setCallsImporting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [callSearch, setCallSearch] = useState("");

  const [categories, setCategories] = useState(INITIAL_CATEGORIES);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newParam, setNewParam] = useState("");
  const [newScore, setNewScore] = useState("");
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [paramSuggestions, setParamSuggestions] = useState([]);
  const [addError, setAddError] = useState("");

  // AI recommendations
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [parametersUpdated, setParametersUpdated] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const handleRunTest = () => {
    setIsTesting(true);
    setTimeout(() => {
      setIsTesting(false);
      setParametersUpdated(false);
      NotificationManager.success(
        "Calls tested against new parameters successfully!",
        "Test Completed",
      );
    }, 1500);
  };

  const [editModal, setEditModal] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const [deleteModal, setDeleteModal] = useState(null);

  // Redirect Agent / Agenth — must be after all hooks
  useEffect(() => {
    if (
      role === "Agent" ||
      role === "Agenth" ||
      role === "Agent Collections" ||
      role.includes("Agent")
    ) {
      history.replace("/my-things/task");
    }
  }, []);

  useEffect(() => {
    services
      .get("api/call_verification/parameters/")
      .then((res) => {
        if (res && Array.isArray(res) && res.length > 0) {
          const mapped = res.map((cat, i) => ({
            id: cat.id || i + 1,
            title: cat.title || cat.name,
            expanded: false,
            items: (cat.items || cat.parameters || []).map((it, j) => ({
              id: it.id || `${i}-${j}`,
              label: it.label || it.name || it.title,
            })),
          }));
          setCategories(mapped);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const isOpen = showAddModal || !!editModal || !!deleteModal;
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAddModal, editModal, deleteModal]);

  const fetchCalls = async (fetchAll = false) => {
    setCallsLoading(true);
    try {
      const res = await services.get(
        "api/call_verification/verification_reports/?page=1",
      );
      const raw = res?.data || res || [];
      const data = fetchAll ? raw : raw.filter((r) => r.call_reviewed === true);
      setCalls(data);
    } catch (err) {
      console.error("Error fetching verification calls:", err);
    } finally {
      setCallsLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls(showAll);
  }, [showAll]);

  const handleImportCalls = () => {
    setCallsImporting(true);
    setTimeout(() => {
      setCallsImporting(false);
      setShowAll(true);
    }, 1500);
  };

  const filteredCalls = calls.filter((c) => {
    const q = callSearch.toLowerCase();
    return (
      !q ||
      (c.call_reference || c.call_id || "").toLowerCase().includes(q) ||
      (c.customer_name || c._call_customer_name || "")
        .toLowerCase()
        .includes(q) ||
      (c.agent || c._call_agent_name || "").toLowerCase().includes(q)
    );
  });

  const toggleCategory = (catId) =>
    setCategories((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, expanded: !c.expanded } : c)),
    );

  const allExpanded =
    categories.length > 0 && categories.every((c) => c.expanded);

  const handleToggleAll = () =>
    setCategories((prev) =>
      prev.map((c) => ({ ...c, expanded: !allExpanded })),
    );

  // ── Dummy AI recommendation data (swap for real API later) ──────────
  const DUMMY_AI_SUGGESTIONS = [
    {
      category: "Opening & Greeting",
      score: "10",
      params: [
        "Did the agent greet the customer by name?",
        "Was the welcome statement clear and professional?",
        "Did the agent introduce themselves and their team?",
      ],
    },
    {
      category: "Promise to Pay",
      score: "20",
      params: [
        "Did the agent confirm the payment date with the customer?",
        "Was the commitment amount clearly stated?",
        "Did the agent follow up on the previous PTP?",
      ],
    },
    {
      category: "Compliance & Disclosure",
      score: "15",
      params: [
        "Did the agent mention the call recording disclosure?",
        "Was FDCPA compliance language used?",
        "Did the agent avoid prohibited debt-collection language?",
      ],
    },
    {
      category: "Closure & Next Steps",
      score: "10",
      params: [
        "Did the agent summarise the agreed action?",
        "Was a follow-up date communicated?",
        "Did the agent thank the customer before ending the call?",
      ],
    },
  ];

  const fetchAiSuggestions = () => {
    setAiLoading(true);
    setAiSuggestions([]);
    // Simulate network delay — replace setTimeout body with a real services.get() call
    setTimeout(() => {
      setAiSuggestions(DUMMY_AI_SUGGESTIONS);
      setAiLoading(false);
    }, 900);
  };

  const openAddModal = () => {
    setNewTitle("");
    setNewParam("");
    setNewScore("");
    setTitleSuggestions([]);
    setParamSuggestions([]);
    setAddError("");
    setShowAddModal(true);
    fetchAiSuggestions();
  };

  const closeAddModal = () => setShowAddModal(false);

  const handleTitleChange = (val) => {
    setNewTitle(val);
    setTitleSuggestions([]);
    if (val.trim().length >= 2) {
      services
        .get(
          `api/call_verification/parameter-suggestions/?query=${encodeURIComponent(val)}&type=title`,
        )
        .then((res) => {
          if (res && Array.isArray(res)) {
            setTitleSuggestions(res.map((r) => r.title || r));
          }
        })
        .catch(() => {});
    }
  };

  const selectTitle = (title) => {
    setNewTitle(title);
    setTitleSuggestions([]);

    services
      .get(
        `api/call_verification/parameter-suggestions/?query=${encodeURIComponent(title)}&type=parameter`,
      )
      .then((res) => {
        if (res && Array.isArray(res)) {
          setParamSuggestions(res.map((r) => r.label || r));
        }
      })
      .catch(() => {});
  };

  const handleParamChange = (val) => {
    setNewParam(val);
    setParamSuggestions([]);
    if (val.trim().length >= 2) {
      const titleCtx = newTitle.trim();
      services
        .get(
          `api/call_verification/parameter-suggestions/?query=${encodeURIComponent(val)}&type=parameter${titleCtx ? `&title=${encodeURIComponent(titleCtx)}` : ""}`,
        )
        .then((res) => {
          if (res && Array.isArray(res)) {
            setParamSuggestions(res.map((r) => r.label || r));
          }
        })
        .catch(() => {});
    }
  };

  const selectParam = (p) => {
    setNewParam(p);
    setParamSuggestions([]);
  };

  const handleAddParameter = () => {
    const trimTitle = newTitle.trim();
    const trimParam = newParam.trim();
    const trimScore = newScore.trim();
    if (!trimTitle || !trimParam || !trimScore) {
      setAddError("Please fill in Category Title, Score and Parameter.");
      return;
    }

    const existing = categories.find(
      (c) => c.title.toLowerCase() === trimTitle.toLowerCase(),
    );

    if (existing) {
      setCategories((prev) =>
        prev.map((c) =>
          c.id === existing.id
            ? {
                ...c,
                expanded: true,
                items: [
                  ...c.items,
                  { id: ++nextId, label: trimParam, score: trimScore },
                ],
              }
            : c,
        ),
      );
    } else {
      const newCat = {
        id: ++nextId,
        title: trimTitle,
        expanded: true,
        items: [{ id: ++nextId, label: trimParam, score: trimScore }],
      };
      setCategories((prev) => [...prev, newCat]);
    }

    NotificationManager.success(
      `"${trimParam}" added to "${trimTitle}".`,
      "Parameter Added",
    );
    closeAddModal();
  };

  const openEdit = (catId, item) => {
    setEditModal({ categoryId: catId, itemId: item.id });
    setEditLabel(item.label);
  };

  const saveEdit = () => {
    if (!editLabel.trim()) return;
    setCategories((prev) =>
      prev.map((c) =>
        c.id === editModal.categoryId
          ? {
              ...c,
              items: c.items.map((it) =>
                it.id === editModal.itemId
                  ? { ...it, label: editLabel.trim() }
                  : it,
              ),
            }
          : c,
      ),
    );
    setParametersUpdated(true);
    NotificationManager.success("Parameter updated.", "Saved");
    setEditModal(null);
  };

  const openDelete = (catId, item) =>
    setDeleteModal({ categoryId: catId, itemId: item.id, label: item.label });

  const confirmDelete = () => {
    setCategories((prev) =>
      prev
        .map((c) =>
          c.id === deleteModal.categoryId
            ? {
                ...c,
                items: c.items.filter((it) => it.id !== deleteModal.itemId),
              }
            : c,
        )
        .filter((c) => c.items.length > 0),
    );
    setParametersUpdated(true);
    NotificationManager.success("Parameter deleted.", "Deleted");
    setDeleteModal(null);
  };

  return (
    <div className="dashboard-body" style={{ paddingBottom: "20px" }}>
      <Header />
      <NavBar />
      <NotificationContainer />

      <div className="container-fluid" style={{ background: "#EEF2F5" }}>
        <div className="my-call bg-white">
          <div
            className="side-panel-with-table clearfix flex"
            style={{ display: "flex" }}
          >
            <MyThingsNav />

            <div className="call-table bg-white" style={{ minHeight: "603px" }}>
              <div
                className="my-calls-column"
                style={{
                  padding: "15px 0px 0px 15px",
                }}
              >
                {/* <div
                  className="calls-top-pannel p-3"
                  style={{ border: "1px solid lightgray" }}
                >
                  <div className="row">
                    <div className="flex justify-between items-center px-6">
                      <div className="pannel-heading clearfix flex items-center">
                        <div className="pannel-heading-icon">
                          <i className="fa fa-th-large" aria-hidden="true"></i>
                        </div>
                        <div className="pannel-heading-info">
                          <h3>Call Report</h3>
      
                        </div>
                      </div>

                      <div className="mr-8">
                        <button
                          id="add-parameter-btn"
                          className="px-5 py-3 mb-4 rounded-lg font-semibold hover:text-white text-gray-700 bg-white run-my-report hover:border-0"
                          onClick={openAddModal}
                        >
                          <i
                            className="fa fa-plus"
                            aria-hidden="true"
                            style={{ marginRight: "6px" }}
                          ></i>
                          Add Parameter
                        </button>
                      </div>
                    </div>
                  </div>
                </div> */}
                {/* ── Top Panel Header ───────────────────────────────────── */}
                <div
                  className="calls-top-pannel"
                  style={{
                    border: "1px solid lightgray",
                    padding: "10px",
                    marginRight: "12px",
                  }}
                >
                  <div className="row">
                    <div className="flex justify-between items-center px-6 mt-4">
                      <div className="pannel-heading clearfix flex items-center">
                        <div className="pannel-heading-icon">
                          <i
                            className="fa fa-file-text-o"
                            aria-hidden="true"
                          ></i>
                        </div>
                        <div className="pannel-heading-info">
                          <h3>Call Report</h3>
                        </div>
                      </div>
                      <div
                        className="mr-8"
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <button
                          id="add-parameter-btn"
                          className="px-5 py-3 mb-4 rounded-lg font-semibold hover:text-white text-gray-700 bg-white run-my-report hover:border-0"
                          onClick={openAddModal}
                        >
                          <i
                            className="fa fa-plus"
                            aria-hidden="true"
                            style={{ marginRight: "6px" }}
                          ></i>
                          New
                        </button>
                        <button
                          id="toggle-view-btn"
                          className="px-5 py-3 mb-4 rounded-lg font-semibold hover:text-white text-gray-700 bg-white run-my-report hover:border-0"
                          onClick={() => setShowCallList(!showCallList)}
                        >
                          {showCallList ? (
                            <>
                              <i
                                className="fa fa-phone"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                              ></i>
                              Hide Call List
                            </>
                          ) : (
                            <>
                              <i
                                className="fa fa-phone"
                                aria-hidden="true"
                                style={{ marginRight: "6px" }}
                              ></i>
                              View Call List
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <>
                  <div className="py-6 pr-6">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "1rem",
                        paddingBottom: "10px",
                        borderBottom: "1px solid #f3f4f6",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#271078"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="9" y="2" width="6" height="4" rx="1" />
                          <path d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
                          <path d="m9 14 2 2 4-4" />
                        </svg>
                        <h4
                          style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: "#271078",
                            margin: 0,
                          }}
                        >
                          Call Report Parameters
                        </h4>
                        {parametersUpdated && (
                          <span
                            style={{
                              fontSize: "11px",
                              backgroundColor: "#fee2e2",
                              color: "#991b1b",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontWeight: "600",
                              marginLeft: "8px",
                              border: "1px solid #f87171",
                              animation: "pulse 2s infinite",
                            }}
                          >
                            Pending Test
                          </span>
                        )}
                        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
                      </div>
                      <div
                        id="view-all-toggle"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          backgroundColor: "#f3f4f6",
                          padding: "5px 14px",
                          borderRadius: "50px",
                          cursor: "pointer",
                          userSelect: "none",
                          border: "1px solid #e5e7eb",
                          transition: "all 0.2s ease",
                        }}
                        onClick={handleToggleAll}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#374151",
                          }}
                        >
                          View All
                        </span>
                        <div
                          style={{
                            width: "34px",
                            height: "18px",
                            backgroundColor: allExpanded
                              ? "#271078"
                              : "#d1d5db",
                            borderRadius: "20px",
                            position: "relative",
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "white",
                              borderRadius: "50%",
                              position: "absolute",
                              top: "3px",
                              left: allExpanded ? "18px" : "3px",
                              transition: "left 0.3s ease",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {categories.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "60px 20px",
                          color: "#9ca3af",
                        }}
                      >
                        <i
                          className="fa fa-inbox"
                          style={{
                            fontSize: "42px",
                            marginBottom: "14px",
                            display: "block",
                          }}
                        ></i>
                        <p style={{ fontSize: "15px" }}>
                          No parameters yet. Click{" "}
                          <strong>Add Parameter</strong> to get started.
                        </p>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {categories.map((cat) => (
                        <div
                          key={cat.id}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "10px",
                            background: "white",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                          }}
                        >
                          <div
                            id={`category-${cat.id}`}
                            onClick={() => toggleCategory(cat.id)}
                            style={{
                              padding: "12px 18px",
                              background: cat.expanded ? "#f9fafb" : "white",
                              borderBottom: cat.expanded
                                ? "1px solid #e5e7eb"
                                : "none",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#f3f4f6")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                cat.expanded ? "#f9fafb" : "white")
                            }
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <h3
                                style={{
                                  margin: 0,
                                  color: "#111827",
                                  fontSize: "16px",
                                  fontWeight: "800",
                                }}
                              >
                                {cat.title}
                              </h3>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  backgroundColor: "#f3f4f6",
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  border: "1px solid #e5e7eb",
                                  fontWeight: "600",
                                }}
                              >
                                {cat.items.length}
                              </span>
                            </div>
                            <svg
                              style={{
                                transition: "transform 0.25s ease",
                                transform: cat.expanded
                                  ? "rotate(0deg)"
                                  : "rotate(-180deg)",
                                color: "#9ca3af",
                              }}
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </div>
                          <div
                            style={{
                              maxHeight: cat.expanded ? "2000px" : "0",
                              opacity: cat.expanded ? 1 : 0,
                              visibility: cat.expanded ? "visible" : "hidden",
                              transition: "all 0.3s ease-in-out",
                              overflow: "hidden",
                            }}
                          >
                            {cat.items.map((item, idx) => (
                              <div
                                key={item.id}
                                id={`param-${item.id}`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "1.25rem",
                                  borderBottom:
                                    idx < cat.items.length - 1
                                      ? "1px solid #f3f4f6"
                                      : "none",
                                  background: "white",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#374151",
                                    fontWeight: "600",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {item.label}
                                </span>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    id={`edit-param-${item.id}`}
                                    title="Edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEdit(cat.id, item);
                                    }}
                                    style={{
                                      background: "white",
                                      border: "1px solid #ddd",
                                      borderRadius: "5px",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      color: "#555",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      transition: "all 0.15s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#271078";
                                      e.currentTarget.style.color = "#271078";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#ddd";
                                      e.currentTarget.style.color = "#555";
                                    }}
                                  >
                                    <i
                                      className="fa fa-pencil"
                                      aria-hidden="true"
                                    ></i>{" "}
                                    Edit
                                  </button>
                                  <button
                                    id={`delete-param-${item.id}`}
                                    title="Delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDelete(cat.id, item);
                                    }}
                                    style={{
                                      background: "white",
                                      border: "1px solid #ddd",
                                      borderRadius: "5px",
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      color: "#555",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      transition: "all 0.15s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#e53e3e";
                                      e.currentTarget.style.color = "#e53e3e";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.borderColor =
                                        "#ddd";
                                      e.currentTarget.style.color = "#555";
                                    }}
                                  >
                                    <i
                                      className="fa fa-trash"
                                      aria-hidden="true"
                                    ></i>{" "}
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>

                {/* ── MODE A: Call List ──────────────────────────────────── */}
                {showCallList && (
                  <>
                    {/* Search + Import row */}
                    <div className="flex justify-end items-center m-10 gap-6">
                      <input
                        onChange={(e) => setCallSearch(e.target.value)}
                        type="text"
                        placeholder="Search by customer name, call ID…"
                        value={callSearch}
                        style={{
                          border: "none",
                          background: "#eef2f5",
                          borderRadius: "10px",
                          width: "250px",
                          outline: "none",
                          paddingLeft: "8px",
                          height: "35px",
                        }}
                      />
                      <button
                        id="run-test-btn"
                        className="hover:ease-in-out hover:duration-600 text-white"
                        onClick={handleRunTest}
                        disabled={!parametersUpdated || isTesting}
                        style={{
                          background: isTesting
                            ? "#6b7280"
                            : parametersUpdated
                              ? "#271078"
                              : "#e5e7eb",
                          color: parametersUpdated ? "white" : "#9ca3af",
                          border: "none",
                          padding: "8px 15px",
                          borderRadius: "10px",
                          cursor:
                            !parametersUpdated || isTesting
                              ? "not-allowed"
                              : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.2s",
                          boxShadow:
                            !parametersUpdated || isTesting
                              ? "none"
                              : "0 4px 6px -1px rgba(39, 16, 120, 0.3)",
                        }}
                      >
                        {isTesting ? (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14px"
                              height="14px"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              style={{ animation: "spin 0.8s linear infinite" }}
                            >
                              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                            </svg>
                            Running Tests...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M5 22h14" />
                              <path d="M5 2h14" />
                              <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                              <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                            </svg>
                            Run Test on New Parameters
                          </>
                        )}
                      </button>
                      <button
                        id="import-calls-btn"
                        className="edit hover:text-white hover:ease-in-out hover:duration-600 text-white"
                        onClick={handleImportCalls}
                        disabled={callsImporting || showAll}
                        style={{
                          border: "1px solid lightgray",
                          padding: "8px 15px",
                          borderRadius: "10px",
                          cursor:
                            callsImporting || showAll
                              ? "not-allowed"
                              : "pointer",
                          opacity: callsImporting || showAll ? 0.75 : 1,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.2s",
                        }}
                      >
                        {callsImporting ? (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14px"
                              height="14px"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              style={{ animation: "spin 0.8s linear infinite" }}
                            >
                              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                            </svg>
                            Importing...
                          </>
                        ) : showAll ? (
                          "All Calls Loaded"
                        ) : (
                          "Import All Calls"
                        )}
                      </button>
                      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                    </div>

                    <div className="overflow-x-scroll">
                      <table
                        className="ss"
                        style={{ border: "1px solid grey" }}
                      >
                        <tr id="black-line">
                          {[
                            "Call Date",
                            "Call ID",
                            "Customer",
                            "Product",
                            "Agent",
                            "Language",
                            "Review",
                            "Action",
                          ].map((item) => (
                            <th
                              key={item}
                              className="left-0 bg-white border-b-2 border-gray-200 px-5 py-4 text-left text-xl font-semibold tracking-wider text-black"
                            >
                              {item}
                            </th>
                          ))}
                        </tr>

                        <tbody>
                          {callsLoading && (
                            <tr>
                              <td colSpan="8" style={{ textAlign: "center" }}>
                                <ClipLoader color="#2056FF" size="50px" />
                              </td>
                            </tr>
                          )}

                          {filteredCalls.length ? (
                            filteredCalls.map((e, i) => (
                              <tr key={i}>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs first-letter">
                                  <div>
                                    <p className="whitespace-no-wrap cursor-pointer text-left text-sky-700">
                                      {e.call_date}
                                    </p>
                                  </div>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs first-letter">
                                  <div>
                                    <p className="whitespace-no-wrap cursor-pointer text-left text-sky-700">
                                      {e.call_id?.split("-")[0]?.toUpperCase()}
                                    </p>
                                  </div>
                                </td>
                                <td
                                  className="border-b border-gray-200 px-5 py-2 text-xs"
                                  id="date"
                                >
                                  <p className="whitespace-nowrap text-left text-gray-900">
                                    {e?.customer_name}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs">
                                  <p className="whitespace-no-wrap text-left text-gray-900">
                                    {e?.product}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs sticky left-0">
                                  <p className="whitespace-nowrap text-left text-gray-900">
                                    {e?.agent}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs sticky left-0">
                                  <p className="whitespace-nowrap text-left text-gray-900">
                                    {e?.language}
                                  </p>
                                </td>
                                <td className="border-b border-gray-200 px-5 py-2 text-xs sticky left-0">
                                  <div className="flex justify-start items-center ml-4">
                                    {e?.call_reviewed ? (
                                      <p
                                        className="font-bold"
                                        style={{
                                          color: "green",
                                          fontSize: "16px",
                                        }}
                                      >
                                        <i
                                          className="fa fa-check-circle"
                                          aria-hidden="true"
                                        ></i>
                                      </p>
                                    ) : (
                                      <p
                                        className="font-bold"
                                        style={{
                                          color: "red",
                                          fontSize: "16px",
                                        }}
                                      >
                                        <i
                                          className="fa fa-times-circle"
                                          aria-hidden="true"
                                        ></i>
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td
                                  className="border-b border-gray-200 px-5 py-2 w-40 text-xs cursor-pointer whitespace-nowrap"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  <button
                                    className="edit hover:text-white hover:ease-in-out hover:duration-600 text-white"
                                    style={{
                                      border: "1px solid lightgray",
                                      padding: "10px 15px",
                                      borderRadius: "10px",
                                      cursor: e.call_reviewed
                                        ? "pointer"
                                        : "not-allowed",
                                      opacity: e.call_reviewed ? 1 : 0.5,
                                    }}
                                    disabled={!e.call_reviewed}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (e.call_reviewed) {
                                        history.push(
                                          `/detailed-reports/${e.id}`,
                                        );
                                      }
                                    }}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : callsLoading ? (
                            ""
                          ) : (
                            <tr>
                              <td colSpan="8" style={{ textAlign: "center" }}>
                                {showAll
                                  ? "No Record Found"
                                  : 'No reviewed calls found. Click "Import All Calls" to load all.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div className="modal my-calls-popup show">
          <div className="backdrop" onClick={closeAddModal}></div>
          <div className="modal-dialog" role="document">
            <div className="my-calls-popup-details">
              <h2>
                <div className="icon-div">
                  <div>
                    <i
                      className="fa fa-plus"
                      style={{ color: "white" }}
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
                Add New Parameter
              </h2>

              <div className="my-calls-form">
                <div className="row">
                  <div className="col-md-6">
                    <div
                      className="form-col clearfix"
                      style={{ position: "relative" }}
                    >
                      <label>
                        Category Title{" "}
                        <span style={{ color: "red", marginLeft: "5px" }}>
                          *
                        </span>
                      </label>
                      <br />
                      <input
                        id="new-category-title"
                        type="text"
                        placeholder="e.g. Collection Tricks"
                        value={newTitle}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        autoComplete="off"
                      />

                      {titleSuggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "white",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            zIndex: 999,
                            maxHeight: "180px",
                            overflowY: "auto",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 12px",
                              fontSize: "11px",
                              color: "#9ca3af",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              borderBottom: "1px solid #f3f4f6",
                            }}
                          >
                            Recommendations
                          </div>
                          {titleSuggestions.map((s, i) => (
                            <div
                              key={i}
                              onClick={() => selectTitle(s)}
                              style={{
                                padding: "9px 14px",
                                fontSize: "13px",
                                cursor: "pointer",
                                color: "#374151",
                                borderBottom:
                                  i < titleSuggestions.length - 1
                                    ? "1px solid #f9fafb"
                                    : "none",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f3f4f6")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              {s}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div
                      className="form-col clearfix"
                      style={{ position: "relative" }}
                    >
                      <label>
                        Score{" "}
                        <span style={{ color: "red", marginLeft: "5px" }}>
                          *
                        </span>
                      </label>
                      <br />
                      <input
                        id="new-score"
                        type="text"
                        placeholder="e.g. 10"
                        value={newScore}
                        onChange={(e) =>
                          setNewScore(e.target.value.replace(/[^0-9]/g, ""))
                        }
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div
                      className="form-col clearfix"
                      style={{ position: "relative" }}
                    >
                      <label>
                        Parameter
                        <span style={{ color: "red", marginLeft: "8px" }}>
                          *
                        </span>
                      </label>
                      <br />
                      <textarea
                        id="new-parameter-text"
                        rows={3}
                        placeholder="e.g. Have you mentioned about the document available?"
                        value={newParam}
                        onChange={(e) => handleParamChange(e.target.value)}
                        style={{ height: "80px", width: "100%" }}
                        autoComplete="off"
                      />

                      {paramSuggestions.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "white",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            zIndex: 999,
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          <div
                            style={{
                              padding: "6px 12px",
                              fontSize: "11px",
                              color: "#9ca3af",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              borderBottom: "1px solid #f3f4f6",
                            }}
                          >
                            Recommendations
                          </div>
                          {paramSuggestions.map((p, i) => (
                            <div
                              key={i}
                              onClick={() => selectParam(p)}
                              style={{
                                padding: "9px 14px",
                                fontSize: "13px",
                                cursor: "pointer",
                                color: "#374151",
                                lineHeight: "1.5",
                                borderBottom:
                                  i < paramSuggestions.length - 1
                                    ? "1px solid #f9fafb"
                                    : "none",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "#f3f4f6")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "white")
                              }
                            >
                              {p}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {addError && (
                  <div className="row">
                    <div className="col-md-12">
                      <p className="errorColor">{addError}</p>
                    </div>
                  </div>
                )}

                <div
                  style={{
                    marginTop: "18px",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    padding: "16px 18px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "14px",
                    }}
                  >
                    <i
                      className="fa fa-lightbulb-o"
                      style={{ fontSize: "16px", color: "#f59e0b" }}
                    ></i>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      Dataklout Recommended Parameters
                    </span>
                  </div>

                  {aiLoading ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "18px 0",
                        color: "#6b7280",
                        fontSize: "13px",
                      }}
                    >
                      <i
                        className="fa fa-circle-o-notch fa-spin"
                        style={{ marginRight: "8px" }}
                      ></i>
                      Loading suggestions...
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        maxHeight: "240px",
                        overflowY: "auto",
                      }}
                    >
                      {aiSuggestions.map((group, gi) => (
                        <div key={gi}>
                          <div
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "#4b5563",
                              marginBottom: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span>{group.category}</span>
                            <span
                              style={{ color: "#d1d5db", fontWeight: "normal" }}
                            >
                              |
                            </span>
                            <span
                              style={{ color: "#6b7280", fontWeight: "normal" }}
                            >
                              Score: {group.score}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {group.params.map((p, pi) => (
                              <div
                                key={pi}
                                onClick={() => {
                                  setNewTitle(group.category);
                                  setNewScore(group.score);
                                  setNewParam(p);
                                  setTitleSuggestions([]);
                                  setParamSuggestions([]);
                                }}
                                style={{
                                  background: "white",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "4px",
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  color: "#374151",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f3f4f6";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "white";
                                }}
                              >
                                {p}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="border"></div>
              <div className="popup-footer">
                <button className="btn" type="button" onClick={closeAddModal}>
                  Cancel
                </button>
                <button
                  id="confirm-add-parameter"
                  className="btn Save"
                  type="button"
                  onClick={handleAddParameter}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal my-calls-popup show">
          <div className="backdrop" onClick={() => setEditModal(null)}></div>
          <div className="modal-dialog" role="document">
            <div className="my-calls-popup-details">
              <h2>
                <div className="icon-div">
                  <div>
                    <i
                      className="fa fa-pencil"
                      style={{ color: "white" }}
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
                Edit Parameter
              </h2>

              <div className="my-calls-form">
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-col clearfix">
                      <label>Parameter Label</label>
                      <br />
                      <textarea
                        id="edit-parameter-label"
                        rows={3}
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        style={{ height: "80px", width: "100%" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border"></div>
              <div className="popup-footer">
                <button
                  className="btn"
                  type="button"
                  onClick={() => setEditModal(null)}
                >
                  Cancel
                </button>
                <button
                  id="save-edit-parameter"
                  className="btn Save"
                  type="button"
                  onClick={saveEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="modal my-calls-popup show">
          <div className="backdrop" onClick={() => setDeleteModal(null)}></div>
          <div className="modal-dialog" role="document">
            <div className="my-calls-popup-details">
              <h2>
                <div className="icon-div">
                  <div>
                    <i
                      className="fa fa-trash"
                      style={{ color: "white" }}
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>
                Delete Parameter
              </h2>

              <div className="my-calls-form">
                <div className="row">
                  <div className="col-md-12">
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#555",
                        marginBottom: "10px",
                      }}
                    >
                      Are you sure you want to delete this parameter?
                    </p>
                    <div
                      style={{
                        padding: "10px 14px",
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#374151",
                      }}
                    >
                      "{deleteModal.label}"
                    </div>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        marginTop: "8px",
                        marginBottom: 0,
                      }}
                    >
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border"></div>
              <div className="popup-footer">
                <button
                  id="cancel-delete"
                  className="btn"
                  type="button"
                  onClick={() => setDeleteModal(null)}
                >
                  Cancel
                </button>
                <button
                  id="confirm-delete"
                  className="btn Save"
                  type="button"
                  onClick={confirmDelete}
                  style={{ backgroundColor: "#e53e3e", borderColor: "#e53e3e" }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MyThing;
